import { useState, useEffect } from "react";
import { useLocation, useRoute, useSearch } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Capacitor } from '@capacitor/core';
import {
  ArrowLeft,
  Star,
  RefreshCw,
  AlertCircle,
  Check,
  PlusCircle,
  Coins,
  Gem, // Add Gem icon
  Backpack
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { CardDeck } from "@shared/schema";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCurrencyBalance } from "@/hooks/use-currency";
import { EnhancedHeader } from "@/components/ui";
import { useLocalDecks } from "@/hooks/use-local-decks";
import { USE_LOCAL_ASSETS, ENABLE_DECK_DOWNLOADS } from "@/config/constants";

// Card types based on provided naming convention
type CardRank =
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "7"
  | "8"
  | "9"
  | "10"
  | "11"
  | "12"
  | "13"
  | "14";
type SuitType = "spades" | "hearts" | "clubs" | "diamonds" | "jokers" | "spar";

export default function DeckDetailPage() {
  const [_, setLocation] = useLocation();
  const [match, params] = useRoute("/deck/:id");
  const search = useSearch();
  const { token } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Download state tracking
  const [downloadProgress, setDownloadProgress] = useState<number | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  // Add a cache busting timestamp for images
  const [imageCacheKey, setImageCacheKey] = useState<string>(() => Date.now().toString());
  
  // Get user's currency balance
  const { data: balance } = useCurrencyBalance();
  
  // Local decks hook for asset resolution
  const { getCardImagePath } = useLocalDecks();
  
  // Helper function to get card image path (local or remote)
  const getCardImageSrc = (cardValue: string): string => {
    if (!deck) return '';
    
    if (USE_LOCAL_ASSETS) {
      return getCardImagePath(deck.deckUrl, cardValue);
    } else {
      return `${deck.deckUrl}/${cardValue}.svg?${imageCacheKey}`;
    }
  };
  
  // Parse returnTo parameter from URL to determine where to navigate back to
  const searchParams = new URLSearchParams(search);
  const returnTo = searchParams.get('returnTo') ?? 'marketplace';

  const deckId = match ? parseInt(params.id) : null;

  // Card display states
  const [selectedSuit, setSelectedSuit] = useState<SuitType | "back">("spar");
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);

  // Card mapping based on the provided file naming convention
  const suits: Record<SuitType, { code: string; symbol: string }> = {
    spades: { code: "A", symbol: "♠️" }, // A for Spades
    hearts: { code: "B", symbol: "♥️" }, // B for Hearts
    clubs: { code: "C", symbol: "♣️" }, // C for Clubs
    diamonds: { code: "D", symbol: "♦️" }, // D for Diamonds
    jokers: { code: "E", symbol: "🃏" }, // E for Jokers
    spar: { code: "S", symbol: "🃏" }, // S for Spar cards
  };

  // Card ranks from 2 to Ace (14)
  const cardRanks: CardRank[] = [
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
    "10",
    "11",
    "12",
    "13",
    "14",
  ];

  // Special cards (jokers)
  const specialCards = [
    { code: "E0", name: "Black Joker" },
    { code: "F0", name: "Red Joker" },
  ];

  // Get rank display name
  const getRankDisplayName = (rank: string): string => {
    switch (rank) {
      case "11":
        return "Jack";
      case "12":
        return "Queen";
      case "13":
        return "King";
      case "14":
        return "Ace";
      default:
        return rank;
    }
  };

  // Fetch deck details
  const { data: deck, isLoading: isDeckLoading } = useQuery<CardDeck>({
    queryKey: ["deck", deckId],
    queryFn: async () => {
      if (!deckId) throw new Error("No deck ID provided");

      let apiUrl = `/api/deck/${deckId}`;
      if (Capacitor.isNativePlatform() && import.meta.env.VITE_SERVER_URL) {
        apiUrl = `${import.meta.env.VITE_SERVER_URL}${apiUrl}`;
      }

      const response = await fetch(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch deck details");
      }

      return response.json();
    },
    enabled: !!deckId,
  });
  
  // Download deck files when viewing deck details
  useEffect(() => {
    if (Capacitor.isNativePlatform() && ENABLE_DECK_DOWNLOADS && deck && deckId) {
      // Import the deckStorageService directly
      import('@/services/deck-storage-service').then(async ({ deckStorageService }) => {
        try {
          // Check if we need to download and track progress
          setIsDownloading(true);
          
          // Use the enhanced method that only downloads if needed
          const wasQueued = await deckStorageService.queueDeckDownloadIfNeeded(deckId, (progress) => {
            setDownloadProgress(progress);
            
            if (progress === 100) {
              // Hide the progress indicator when done
              setTimeout(() => {
                setIsDownloading(false);
                setDownloadProgress(null);
              }, 500);
            }
          });
          
          // If no download was needed (already downloaded), hide indicators
          if (!wasQueued) {
            setIsDownloading(false);
            setDownloadProgress(null);
          }
        } catch (error) {
          console.error("Failed to download deck:", error);
          setIsDownloading(false);
        }
      }).catch(error => {
        console.error("Failed to import deck storage service:", error);
        setIsDownloading(false);
      });
    }
  }, [deck, deckId]);

  // Fetch user decks to check if this deck is saved and if it's the default
  const { data: userDecks } = useQuery({
    queryKey: ["user-decks"],
    queryFn: async () => {
      let apiUrl = "/api/deck/my-decks";
      if (Capacitor.isNativePlatform() && import.meta.env.VITE_SERVER_URL) {
        apiUrl = `${import.meta.env.VITE_SERVER_URL}${apiUrl}`;
      }

      const response = await fetch(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error("Failed to fetch user decks");
      return response.json();
    },
  });

  const isSaved = userDecks?.some(
    (userDeck: any) => userDeck.cardDeckId === deckId,
  );

  const isDefault = userDecks?.some(
    (userDeck: any) => userDeck.cardDeckId === deckId && userDeck.default,
  );

  // Check if this is the system default deck
  const isSystemDefault = deck?.id === -1;

  // Function to get membership badge styling
  const getMembershipBadge = (membership: string) => {
    if (isSystemDefault) {
      return { bg: "bg-blue-500/20", text: "text-blue-500", label: "System Default" };
    }
    switch (membership) {
      case "free":
        return { bg: "bg-green-500/20", text: "text-green-500", label: "Free" };
      case "premium":
        return {
          bg: "bg-amber-500/20",
          text: "text-amber-500",
          label: "Premium",
        };
      case "exclusive":
        return {
          bg: "bg-purple-500/20",
          text: "text-purple-500",
          label: "Exclusive",
        };
      default:
        return {
          bg: "bg-gray-500/20",
          text: "text-gray-500",
          label: membership,
        };
    }
  };

  // Handler for set as default
  const handleSetDefault = async () => {
    if (!deckId || isSystemDefault) return;

    try {
      let apiUrl = `/api/deck/set-default/${deckId}`;
      if (Capacitor.isNativePlatform() && import.meta.env.VITE_SERVER_URL) {
        apiUrl = `${import.meta.env.VITE_SERVER_URL}${apiUrl}`;
      }

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error("Failed to set deck as default");

      await queryClient.invalidateQueries({ queryKey: ["default-deck"] });
      await queryClient.invalidateQueries({ queryKey: ["user-decks"] });

      // Download the deck when it's set as default (only if not already downloaded)
      if (Capacitor.isNativePlatform() && deck) {
        try {
          // Import deck storage service directly
          const { deckStorageService } = await import('@/services/deck-storage-service');
          
          // Check if download is needed before showing toast
          const wasQueued = await deckStorageService.queueDeckDownloadIfNeeded(deckId, (progress) => {
            // If download started, show progress in the toast
            if (progress === 100) {
              toast({
                title: "Deck Downloaded",
                description: "Default deck is ready for offline use!",
                duration: 3000,
              });
            }
          });
          
          // Only show the downloading toast if a download was actually queued
          if (wasQueued) {
            toast({
              title: "Downloading Default Deck",
              description: "Downloading deck files for offline use...",
              duration: 5000,
            });
          }
        } catch (downloadError) {
          console.error("Failed to download deck:", downloadError);
        }
      }

      toast({
        title: "Success",
        description: "Deck set as default!",
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to set deck as default";
      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
    }
  };

  // Handle remove deck
  const handleRemoveDeck = async () => {
    if (!deckId || isSystemDefault) return;

    try {
      let apiUrl = `/api/deck/remove/${deckId}`;
      if (Capacitor.isNativePlatform() && import.meta.env.VITE_SERVER_URL) {
        apiUrl = `${import.meta.env.VITE_SERVER_URL}${apiUrl}`;
      }

      const response = await fetch(apiUrl, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error("Failed to remove deck");

      await queryClient.invalidateQueries({ queryKey: ["user-decks"] });

      toast({
        title: "Success",
        description: "Deck removed from your collection!",
      });

      // Navigate back based on returnTo parameter
      if (returnTo === 'settings') {
        setLocation("/settings");
      } else {
        setLocation("/deck-marketplace");
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to remove deck";
      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
    }
  };

  // Handler to show purchase dialog or directly acquire free decks
  const handleDeckAction = async () => {
    if (!deckId || isSystemDefault) return;
    
    // Initialize audio system when user takes an action
    import('@/utils/sound-utils').then(({ initSoundSystem, playGameSound }) => {
      // Initialize sound system and play a quick UI sound
      initSoundSystem();
      playGameSound('ui.button').catch(console.error);
    });
    
    const price = deck?.price ? Math.floor(parseFloat(deck.price)) : 0;
    
    // If it's a free deck or has price 0, acquire directly
    if (!price || price === 0) {
      await acquireDeck();
      return;
    }
    
    // Otherwise, show purchase confirmation dialog
    setPurchaseDialogOpen(true);
  };
  
  // Handler for acquiring decks (both free and paid)
  const acquireDeck = async () => {
    if (!deckId || !deck) return; // Ensure deck is loaded
    
    setIsSubmitting(true);
    const price = Math.floor(parseFloat(deck.price));
    const currencyType = deck.currencyType || 'regular';

    try {
      let apiUrl = `/api/deck/acquire/${deckId}`;
      if (Capacitor.isNativePlatform() && import.meta.env.VITE_SERVER_URL) {
        apiUrl = `${import.meta.env.VITE_SERVER_URL}${apiUrl}`;
      }

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        
        if (response.status === 400 && errorData.message === "Insufficient funds") {
          toast({
            variant: "destructive",
            title: "Insufficient Funds",
            description: `You don't have enough ${currencyType === 'premium' ? 'gems' : 'coins'} to purchase this deck.`,
          });
          setPurchaseDialogOpen(false); // Close dialog on insufficient funds
          return;
        }
        
        throw new Error(errorData.message ?? "Failed to acquire deck");
      }
      
      const data = await response.json();
      
      await queryClient.invalidateQueries({ queryKey: ["user-decks"] });
      
      // If it was a purchase, also invalidate currency balance
      if (data.isPurchase) {
        await queryClient.invalidateQueries({ queryKey: ['/api/currency/balance'] });
        await queryClient.invalidateQueries({ queryKey: ['/api/currency/transactions'] });
        
        toast({
          title: "Purchase Successful",
          description: `You've purchased ${deck.name} for ${data.price} ${currencyType === 'premium' ? 'gems' : 'coins'}.`,
        });
      } else {
        toast({
          title: "Success",
          description: "Deck added to your collection!",
        });
      }
      
      setPurchaseDialogOpen(false);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message ?? "Failed to acquire deck",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isDeckLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-gray-900 to-black text-white p-4 md:p-6 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <RefreshCw className="h-12 w-12 animate-spin text-primary mb-4" />
          <p className="text-muted-foreground">Loading deck details...</p>
        </div>
      </div>
    );
  }

  if (!deck) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-gray-900 to-black text-white p-4 md:p-6 flex items-center justify-center">
        <div className="bg-black/80 rounded-lg border border-destructive/40 p-6 max-w-md text-center">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Deck Not Found</h2>
          <p className="text-muted-foreground mb-4">
            The deck you're looking for doesn't exist or may have been removed.
          </p>
          <Button
            onClick={() => {
              if (returnTo === 'settings') {
                setLocation("/settings");
              } else {
                setLocation("/deck-marketplace");
              }
            }}
            className="bg-primary hover:bg-primary/90 text-white"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {returnTo === 'settings' ? 'Back to Settings' : 'Back to Marketplace'}
          </Button>
        </div>
      </div>
    );
  }

  const membershipBadge = getMembershipBadge(deck.membership);
  const price = deck.price ? Math.floor(parseFloat(deck.price)) : 0;
  const currencyType = deck.currencyType || 'regular';
  const userBalanceAmount = currencyType === 'premium' ? balance?.premiumCurrency : balance?.currency;
  const hasSufficientFunds = userBalanceAmount !== undefined && userBalanceAmount >= price;

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 25 },
    },
  };

  return (
    <div> 

      <EnhancedHeader
        title="Deck Details"
        backPath={returnTo === 'settings' ? '/settings' : '/deck-marketplace'}
        rightContent={
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full bg-transparent hover:bg-black/60 text-white w-9 h-9 flex items-center justify-center p-0"
            onClick={() => {
              setLocation("/settings");
            }}>
              <Backpack className="h-6 w-6" />
          </Button>
        
        }
      />
      
        
        <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-gray-900 to-black text-white p-4 md:p-6 pb-20">
        <div className="max-w-7xl mx-auto">
        

        {/* Main content */}
        <div className="grid md:grid-cols-[1fr_2fr] gap-8">
          {/* Left column - Deck info */}
          <div className="bg-black/60 backdrop-blur-sm rounded-xl border border-primary/30 p-6">
            {/* Deck title and badges */}
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-2xl font-bold">{deck.name}</h2>
                {isDefault && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge className="bg-yellow-500/20 text-yellow-500 font-medium">
                          <Star className="h-3 w-3 mr-1 fill-yellow-500" />
                          Default
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>This is your default deck</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                <Badge
                  className={cn(
                    "font-medium",
                    membershipBadge.bg,
                    membershipBadge.text
                  )}
                >
                  {membershipBadge.label}
                </Badge>
                
                <Badge variant="outline" className="bg-black/20 capitalize">
                  {deck.deckType}
                </Badge>
                {deck.isTradeable && (
                  <Badge
                    variant="outline"
                    className="bg-blue-500/10 text-blue-400"
                  >
                    Tradeable
                  </Badge>
                )}
                {deck.managerMode && (
                  <Badge
                    variant="outline"
                    className="bg-purple-500/10 text-purple-400"
                  >
                    Manager Mode
                  </Badge>
                )}
              </div>

              {/* Price display with correct icon */}
              {price > 0 && (
                <div className="flex justify-end mb-4">
                  <div className="bg-amber-600/20 text-amber-400 px-4 py-2 rounded-lg inline-flex items-center">
                    <span className="text-xl font-bold mr-2">{price}</span>
                    {currencyType === 'premium' ? (
                      <Gem className="h-5 w-5 text-purple-400" />
                    ) : (
                      <Coins className="h-5 w-5" />
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Deck preview */}
            <div className="relative h-64 bg-gradient-to-br from-black to-black/20 rounded-lg mb-6 flex justify-center items-center overflow-hidden">
              {/* Download progress overlay */}
              {isDownloading && (
                <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center z-20">
                  <RefreshCw className="h-8 w-8 animate-spin text-primary mb-2" />
                  {downloadProgress !== null && (
                    <div className="w-3/4 max-w-48">
                      <div className="h-1.5 w-full bg-gray-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary rounded-full transition-all duration-300 ease-out"
                          style={{ width: `${downloadProgress}%` }}
                        />
                      </div>
                      <p className="text-xs text-center mt-1 text-primary-foreground">
                        {downloadProgress}% downloaded
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              <div className="relative w-40 h-56">
                {["A13", "B13", "C13", "D13"].map((card, index) => (
                  <motion.div
                    key={card}
                    className="absolute inset-0 w-full h-full"
                    initial={{ rotate: 0, translateX: 0 }}
                    animate={{
                      rotate: `${(index - 1.5) * 5}deg`,
                      translateX: `${(index - 1.5) * 5}px`,
                      translateY: hoveredCard === card ? -10 : 0,
                    }}
                    whileHover={{ translateY: -10 }}
                    onHoverStart={() => setHoveredCard(card)}
                    onHoverEnd={() => setHoveredCard(null)}
                    transition={{ type: "spring", stiffness: 300, damping: 15 }}
                  >
                    <img
                      src={getCardImageSrc(card)}
                      alt={`Preview card ${index + 1}`}
                      className="w-full h-full object-contain rounded-md shadow-xl"
                      style={{ zIndex: 10 - index }}
                      onError={(e) => {
                        e.currentTarget.src = getCardImageSrc('back');
                      }}
                    />
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Action buttons */}
            <div className="space-y-3">
              {!isSaved ? (
                <Button
                  className="w-full bg-primary hover:bg-primary/90 text-white font-medium shadow-md"
                  onClick={handleDeckAction} // Use the updated handler
                  disabled={price > 0 && !hasSufficientFunds} // Disable if insufficient funds
                >
                  {price > 0 ? (
                    <>
                      <Coins className="h-5 w-5 mr-2" /> {/* Or Gem based on currencyType */} 
                      Purchase Deck
                    </>
                  ) : (
                    <>
                      <PlusCircle className="h-5 w-5 mr-2" />
                      Add to Collection (Free)
                    </>
                  )}
                </Button>
              ) : (
                <>
                  {!isDefault && (
                    <Button
                      className="w-full bg-yellow-500/90 hover:bg-yellow-500 text-white font-medium shadow-md"
                      onClick={handleSetDefault}
                    >
                      <Star className="h-5 w-5 mr-2" />
                      Set as Default Deck
                    </Button>
                  )}
                  {isDefault && (
                    <div className="flex items-center justify-center p-3 bg-green-600/30 text-green-300 rounded-lg font-medium">
                      <Check className="h-5 w-5 mr-2" />
                      Currently Active Default Deck
                    </div>
                  )}
                  
                  {/* Redownload button - only show on native platform for saved decks and when deck downloads are enabled */}
                  {Capacitor.isNativePlatform() && ENABLE_DECK_DOWNLOADS && isSaved && !isDownloading && (
                    <Button
                      variant="outline"
                      className="w-full bg-blue-500/10 border-blue-500/40 text-blue-400 hover:bg-blue-500/20 hover:text-blue-300 font-medium"
                      onClick={async () => {
                        if (!deckId) return;
                        
                        try {
                          // Import the deckStorageService directly
                          const { deckStorageService } = await import('@/services/deck-storage-service');
                          
                          // Show downloading state
                          setIsDownloading(true);
                          setDownloadProgress(0);
                          
                          // Force redownload by setting forceDownload to true
                          await deckStorageService.queueDeckDownloadIfNeeded(deckId, (progress) => {
                            setDownloadProgress(progress);
                            
                            if (progress === 100) {
                              // Hide the progress indicator when done and show success toast
                              setTimeout(() => {
                                setIsDownloading(false);
                                setDownloadProgress(null);
                                setImageCacheKey(Date.now().toString()); // Update cache key to refresh images
                                
                                toast({
                                  title: "Download Complete",
                                  description: "Deck assets have been successfully redownloaded.",
                                });
                              }, 500);
                            }
                          }, true); // The true parameter forces redownload
                          
                        } catch (error) {
                          console.error("Failed to redownload deck:", error);
                          setIsDownloading(false);
                          setDownloadProgress(null);
                          
                          toast({
                            variant: "destructive",
                            title: "Download Failed",
                            description: "There was an error redownloading the deck assets.",
                          });
                        }
                      }}
                    >
                      <RefreshCw className="h-5 w-5 mr-2" />
                      Redownload Deck Assets
                    </Button>
                  )}
                  
                  <Button
                    variant="outline"
                    className="w-full bg-red-500/10 border-red-500/40 text-red-400 hover:bg-red-500/20 hover:text-red-300 font-medium"
                    onClick={handleRemoveDeck}
                  >
                    Remove from Collection
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Right column - Card showcase */}
          <div className="bg-black/60 backdrop-blur-sm rounded-xl border border-primary/30 p-6">
            <h3 className="text-xl font-bold mb-4">Card Preview</h3>

            {/* Suit selector */}
            <div className="flex flex-wrap gap-2 mb-6">
              <Button
                variant={selectedSuit === "spar" ? "default" : "outline"}
                className={
                  selectedSuit === "spar"
                    ? "bg-primary text-primary-foreground font-medium shadow-md"
                    : "bg-black/30 border-primary/30 hover:bg-primary/20 text-white"
                }
                onClick={() => setSelectedSuit("spar")}
              >
                <span className="mr-1">🃏</span>
                Spar
              </Button>
              <Button
                variant={selectedSuit === "back" ? "default" : "outline"}
                className={
                  selectedSuit === "back"
                    ? "bg-primary text-primary-foreground font-medium shadow-md"
                    : "bg-black/30 border-primary/30 hover:bg-primary/20 text-white"
                }
                onClick={() => setSelectedSuit("back")}
              >
                <span className="mr-2">{'\u{1F0A0}'}</span>
                Back
              </Button>
              {Object.entries(suits).map(([suitName, suitInfo]) => (
                <Button
                  key={suitName}
                  variant={selectedSuit === suitName ? "default" : "outline"}
                  className={
                    selectedSuit === suitName
                      ? "bg-primary text-primary-foreground font-medium shadow-md"
                      : "bg-black/30 border-primary/30 hover:bg-primary/20 text-white"
                  }
                  onClick={() => setSelectedSuit(suitName as SuitType)}
                >
                  <span className="mr-1">{suitInfo.symbol}</span>
                  {suitName.charAt(0).toUpperCase() + suitName.slice(1)}
                </Button>
              ))}
            </div>

            <Separator className="mb-6 bg-white/10" />

            {/* Card grid */}
            {selectedSuit === "spar" ? (
              <motion.div
                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-6 gap-3"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                key="spar"
              >
                {/* Display only K, Q, J, 10, 9, 8, 7, 6 of hearts for Spar */}
                {["13", "12", "11", "10", "9", "8", "7", "6"].map((rank) => (
                  <motion.div
                    key={`spar-${rank}`}
                    variants={itemVariants}
                    className="aspect-[2/3] bg-black/50 rounded-md overflow-hidden hover:bg-black/70 transition-colors group relative"
                    whileHover={{ y: -5, scale: 1.05, zIndex: 5 }}
                  >
                    <div className="w-full h-full p-2 flex items-center justify-center">
                      <img
                        src={getCardImageSrc(`B${rank}`)}
                        alt={`${getRankDisplayName(rank)} of hearts`}
                        className="w-full h-full object-contain rounded shadow-md group-hover:shadow-xl transition-shadow"
                        onError={(e) => {
                          e.currentTarget.src = getCardImageSrc('back');
                        }}
                      />
                      <div className="absolute bottom-1 left-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity text-xs bg-black/70 text-center py-1 rounded">
                        {getRankDisplayName(rank)}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            ) : selectedSuit !== "jokers" && selectedSuit !== "back" ? (
              <motion.div
                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7 gap-3"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                key={selectedSuit}
              >
                {/* Reversed order to show cards from highest to lowest */}
                {[...cardRanks].reverse().map((rank) => (
                  <motion.div
                    key={`${selectedSuit}-${rank}`}
                    variants={itemVariants}
                    className="aspect-[2/3] bg-black/50 rounded-md overflow-hidden hover:bg-black/70 transition-colors group relative"
                    whileHover={{ y: -5, scale: 1.05, zIndex: 5 }}
                  >
                    <div className="w-full h-full p-2 flex items-center justify-center">
                      <img
                        src={getCardImageSrc(`${suits[selectedSuit].code}${rank}`)}
                        alt={`${getRankDisplayName(rank)} of ${selectedSuit}`}
                        className="w-full h-full object-contain rounded shadow-md group-hover:shadow-xl transition-shadow"
                        onError={(e) => {
                          e.currentTarget.src = getCardImageSrc('back');
                        }}
                      />
                      <div className="absolute bottom-1 left-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity text-xs bg-black/70 text-center py-1 rounded">
                        {getRankDisplayName(rank)}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            ) : selectedSuit === "back" ? (
              <motion.div
                className="flex justify-center gap-8 py-8"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                key="back"
              >
                <motion.div
                  variants={itemVariants}
                  className="aspect-[2/3] w-36 bg-black/50 rounded-md overflow-hidden hover:bg-black/70 transition-colors group relative"
                  whileHover={{ y: -10, scale: 1.1, zIndex: 5, rotateY: 5 }}
                >
                  <div className="w-full h-full p-2 flex items-center justify-center">
                    <img
                      src={getCardImageSrc('back')}
                      alt="Card Back"
                      className="w-full h-full object-contain rounded shadow-xl"
                    />
                    <div className="absolute bottom-2 left-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-sm bg-black/70 text-center py-1 rounded-md font-medium">
                      Card Back
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            ) : (
              <motion.div
                className="flex justify-center gap-8 py-8"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                key="jokers"
              >
                {specialCards.map((joker) => (
                  <motion.div
                    key={joker.code}
                    variants={itemVariants}
                    className="aspect-[2/3] w-36 bg-black/50 rounded-md overflow-hidden hover:bg-black/70 transition-colors group relative"
                    whileHover={{ y: -10, scale: 1.1, zIndex: 5, rotateY: 5 }}
                  >
                    <div className="w-full h-full p-2 flex items-center justify-center">
                      <img
                        src={getCardImageSrc(joker.code)}
                        alt={joker.name}
                        className="w-full h-full object-contain rounded shadow-xl"
                        onError={(e) => {
                          e.currentTarget.src = getCardImageSrc('back');
                        }}
                      />
                      <div className="absolute bottom-2 left-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-sm bg-black/70 text-center py-1 rounded-md font-medium">
                        {joker.name}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </div>
        </div>
      
      </div>
      
      {/* Purchase Confirmation Dialog */}
      <Dialog open={purchaseDialogOpen} onOpenChange={setPurchaseDialogOpen}>
        <DialogContent className="bg-black/90 border border-green-600/30">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">Confirm Purchase</DialogTitle>
            <DialogDescription className="text-gray-300">
              Are you sure you want to purchase <span className="text-white font-medium">{deck?.name}</span>?
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="flex justify-between items-center mb-4">
              <span className="text-gray-300">Price:</span>
              <div className={`flex items-center ${currencyType === 'premium' ? 'text-purple-400' : 'text-amber-400'}`}>
                <span className="text-xl font-bold mr-2">{price}</span>
                {currencyType === 'premium' ? (
                  <Gem className="h-5 w-5" />
                ) : (
                  <Coins className="h-5 w-5" />
                )}
              </div>
            </div>
            
            <div className="flex justify-between items-center mb-4">
              <span className="text-gray-300">Your balance:</span>
              <div className={`flex items-center ${currencyType === 'premium' ? 'text-purple-400' : 'text-amber-400'}`}>
                <span className="font-medium mr-1">{userBalanceAmount ?? 0}</span>
                 {currencyType === 'premium' ? (
                  <Gem className="h-4 w-4" />
                ) : (
                  <Coins className="h-4 w-4" />
                )}
              </div>
            </div>
            
            {!hasSufficientFunds && (
              <div className="bg-red-900/20 border border-red-700/40 text-red-300 p-3 rounded-md text-sm mb-4">
                <AlertCircle className="h-4 w-4 inline mr-1" /> 
                Insufficient funds. You need {price - (userBalanceAmount ?? 0)} more {currencyType === 'premium' ? 'gems' : 'coins'}.
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPurchaseDialogOpen(false)}
              className="border-gray-700 bg-transparent text-gray-300 hover:bg-gray-800 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={acquireDeck}
              disabled={isSubmitting || !hasSufficientFunds}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {isSubmitting ? 'Processing...' : 'Confirm Purchase'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>

    </div>
    
  );
}

// Enhanced mobile header for deck detail page
interface EnhancedDetailHeaderProps {
  readonly title: string;
  readonly backPath: string;
}

function EnhancedDetailHeader({ title, backPath }: Readonly<EnhancedDetailHeaderProps>) {
  const [_, setLocation] = useLocation();
  const [safePaddingTop, setSafePaddingTop] = useState(0);
  
  // Add a debugging function to clearly log navigation attempts
  const handleBackClick = () => {
    console.log("Back button clicked, navigating to:", backPath);
    
    // Force navigation with window.location as a fallback if setLocation doesn't work
    if (!backPath) {
      console.warn("backPath is empty, defaulting to marketplace");
      setLocation("/deck-marketplace");
      return;
    }
    
    // Try direct navigation
    setLocation(backPath);
  };
  
  useEffect(() => {
    // When in a Capacitor environment, get the safe area insets
    const getSafeAreaInsets = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          // Set a higher default safe area to avoid status bar overlap
          setSafePaddingTop(50);
          
          // Attempt to use status bar information if available
          const capacitorStatusBar = (window as any).Capacitor?.Plugins?.StatusBar;
          if (capacitorStatusBar?.getInfo) {
            try {
              const info = await capacitorStatusBar.getInfo();
              if (info?.height) {
                setSafePaddingTop(info.height + 5);
              }
            } catch (error) {
              console.log("StatusBar plugin not available", error);
            }
          }
        } catch (error) {
          console.error("Error getting safe area insets:", error);
          setSafePaddingTop(50);
        }
      } else {
        setSafePaddingTop(16);
      }
    };
    
    getSafeAreaInsets();
  }, []);
  
  // Parse the title to separate deck name from "Deck" word
  const titleParts = title.split(' ');
  const deckName = titleParts.filter(part => part.toLowerCase() !== 'deck').join(' ');
  
  return (
    <div 
      className={cn(
        "sticky top-0 z-30 transition-all duration-300",
        
      )}
      style={{ paddingTop: safePaddingTop ? `${safePaddingTop}px` : undefined }}
    >
      <div className="flex items-center px-4 py-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleBackClick}
          className="rounded-full bg-transparent hover:bg-black/60 text-white w-9 h-9 mr-2 flex items-center justify-center p-0 z-10"
        >
          <ArrowLeft className="h-6 w-6" />
        </Button>
        
        <h1 className="text-2xl font-bold flex-1 text-center ml-[-2.25rem]">
          <span className="text-white">{deckName}</span>
          <span className="text-green-500"> Deck</span>
        </h1>
        
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full bg-transparent hover:bg-black/60 text-white w-9 h-9 flex items-center justify-center p-0"
          >
            <Star className="h-6 w-6" />
          </Button>
        </div>
      </div>
    </div>
  );
}
